import type { OrderDto, PlanDto, PlanId } from "@workcost/shared";
import { apiFetch } from "./api";
import { EstimateApiError } from "./estimateApi";

/** เฉพาะ dev และตั้ง VITE_MOCK_API=1 เท่านั้น (นิพจน์นี้เป็น false ตายตัวใน production build) */
async function loadMock(): Promise<typeof import("../mock/billing") | null> {
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") return import("../mock/billing");
  return null;
}

async function call<T>(path: string, init: { method: string; json?: unknown; auth?: boolean } = { method: "GET" }): Promise<T> {
  const res = await apiFetch(path, init);
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; reason?: ConstructorParameters<typeof EstimateApiError>[2] };
    throw new EstimateApiError(res.status, b.error ?? "error", b.reason);
  }
  return body as T;
}

const enc = encodeURIComponent;

/** GET /plans (สาธารณะ) */
export async function fetchPlans(): Promise<PlanDto[]> {
  const mock = await loadMock();
  if (mock) return mock.plans();
  return (await call<{ items: PlanDto[] }>("/plans", { method: "GET", auth: false })).items;
}

export interface PublicConfig {
  default_overhead_pct: number;
}

/** GET /config (สาธารณะ) */
export async function fetchConfig(): Promise<PublicConfig> {
  const mock = await loadMock();
  if (mock) return mock.config();
  return call<PublicConfig>("/config", { method: "GET", auth: false });
}

/** POST /orders (ใช้คำสั่งซื้อเดิมถ้ายังไม่หมดอายุ, 409 downgrade_blocked ถ้าซื้อต่ำกว่าที่ใช้อยู่) */
export async function createOrder(plan: PlanId): Promise<OrderDto> {
  const mock = await loadMock();
  if (mock) return mock.createOrder(plan);
  return call<OrderDto>("/orders", { method: "POST", json: { plan } });
}

/** GET /orders/{id} (poll ทุก 3 วินาทีที่หน้าชำระเงิน) */
export async function getOrder(id: string): Promise<OrderDto> {
  const mock = await loadMock();
  if (mock) return mock.getOrder(id);
  return call<OrderDto>(`/orders/${enc(id)}`, { method: "GET" });
}

/** POST /orders/{id}/declare: ลูกค้าแจ้งว่าชำระแล้ว (เป็นคำแจ้งให้แอดมินตรวจ ไม่ใช่การยืนยันจากธนาคาร และไม่เปิดแพ็กเกจ) เรียกซ้ำได้ผลเดิม */
export async function declarePayment(id: string): Promise<OrderDto> {
  const mock = await loadMock();
  if (mock) return mock.declarePayment(id);
  return call<OrderDto>(`/orders/${enc(id)}/declare`, { method: "POST", json: {} });
}

/** POST /orders/{id}/cancel: ยกเลิกคำสั่งซื้อที่ยังไม่แจ้งชำระ */
export async function cancelOrder(id: string): Promise<OrderDto> {
  const mock = await loadMock();
  if (mock) return mock.cancelOrder(id);
  return call<OrderDto>(`/orders/${enc(id)}/cancel`, { method: "POST", json: {} });
}

/** GET /orders (ประวัติ ใหม่ไปเก่า) */
export async function listOrders(): Promise<OrderDto[]> {
  const mock = await loadMock();
  if (mock) return mock.listOrders();
  return (await call<{ items: OrderDto[] }>("/orders", { method: "GET" })).items;
}
